import { GetCmsPromotionItem, GetCmsPromotions } from '@cbgms/api/modules/cms/requests'
import { useInfiniteQuery, useQuery, useQueryClient } from '@cbgms/base/http/use-query'
import { useLocaleContext } from '@cbgms/base/locale/LocaleContext'
import { useOrderAccountsContext } from 'app/workorder/contexts/use-order-account-context'
import { useMemo } from 'react'
import { useDispatch } from 'react-redux'

const CMSPromotionsListCacheKey = (orderAccountUUID: string, locale: string, perPage: number) => [
  'CMS',
  'PROMOTIONS_ITEMS',
  'LIST',
  orderAccountUUID,
  locale,
  perPage
]

const CMSPromotionsItemCacheKey = (orderAccountUUID: string, locale: string, id: string) => [
  'CMS',
  'PROMOTIONS_ITEMS',
  'item',
  orderAccountUUID,
  locale,
  id
]

export const useCMSPromotionsList = (perPage = 100) => {
  const dispatch = useDispatch()
  const { locale } = useLocaleContext()
  const { selectedOrderAccount } = useOrderAccountsContext()
  const queryClient = useQueryClient()

  const queryData = useInfiniteQuery(
    CMSPromotionsListCacheKey(selectedOrderAccount.UUID, locale, perPage),
    async ({ pageParam = 0 }) => {
      const { Data } = await dispatch(GetCmsPromotions({ orderAccountUUID: selectedOrderAccount.UUID, page: pageParam + 1, perPage }))
      // after fetching apply some pre cached data so we don't need to fetch it every time
      Data.Promotions.forEach(item => queryClient.setQueryData(CMSPromotionsItemCacheKey(selectedOrderAccount.UUID, locale, item.ID), item))
      return { promotions: Data.Promotions, nextCursor: pageParam + 1 }
    },
    { getNextPageParam: lastPage => (lastPage.promotions.length ? lastPage.nextCursor : undefined) }
  )

  const allPromotionItems = useMemo(() => (queryData.data?.pages || []).flatMap(p => p.promotions), [queryData.data?.pages])
  return useMemo(() => ({ ...queryData, allPromotionItems, hasNextPage: !!queryData.hasNextPage }), [queryData])
}

export const useCMSPromotionItem = (id: string) => {
  const dispatch = useDispatch()
  const { locale } = useLocaleContext()
  const { selectedOrderAccount } = useOrderAccountsContext()

  return useQuery(CMSPromotionsItemCacheKey(selectedOrderAccount.UUID, locale, id), async () => {
    const { Data } = await dispatch(GetCmsPromotionItem({ orderAccountUUID: selectedOrderAccount.UUID, id }))
    return Data
  })
}
